/**
 * Chat Widget for n8n Refund Workflow
 * Handles chat interaction with polling for response status
 */
const ChatWidget = (function() {
  'use strict';

  // Configuration
  let config = {
    webhookUrl: '',
    statusUrl: 'https://hemorrhoid.app.n8n.cloud/webhook/get-request-status',
    agentName: 'Customer Support',
    agentStatus: 'Online',
    agentInitials: 'CS',
    welcomeMessage: '',
    pollingInterval: 1500,
    maxPollingAttempts: 40
  };

  // State
  let sessionId = null;
  let isPolling = false;
  let pollingAttempts = 0;
  let lastKnownResponse = null; // Track last known Response to detect new responses
  let floatingWidgetContainer = null;
  let isWidgetOpen = false;
  let responseHandled = false; // Track if response has already been handled

  /**
   * Generate a unique session ID
   */
  function generateSessionId() {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11);
  }

  /**
   * Get or create session ID
   */
  function getSessionId() {
    if (!sessionId) {
      sessionId = sessionStorage.getItem('chat_session_id');
      if (!sessionId) {
        sessionId = generateSessionId();
        sessionStorage.setItem('chat_session_id', sessionId);
      }
    }
    return sessionId;
  }

  /**
   * Format current time
   */
  function formatTime() {
    const now = new Date();
    return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  /**
   * Create typing indicator element
   * @param {string} statusText - Optional status text to display with the typing dots
   */
  function createTypingIndicator(statusText = null) {
    const indicator = document.createElement('div');
    indicator.className = 'message typing-message';

    let contentHtml;
    if (statusText) {
      contentHtml = `
        <div class="status-text-container">
          <span class="status-text">${escapeHtml(statusText)}</span>
          <span class="typing-dots-inline">
            <span class="typing-dot"></span>
            <span class="typing-dot"></span>
            <span class="typing-dot"></span>
          </span>
        </div>
      `;
    } else {
      contentHtml = `
        <div class="typing-dots-container">
          <span class="typing-dot"></span>
          <span class="typing-dot"></span>
          <span class="typing-dot"></span>
        </div>
      `;
    }

    indicator.innerHTML = `
      <div class="message-avatar">${config.agentInitials}</div>
      <div class="message-content">
        ${contentHtml}
      </div>
    `;
    return indicator;
  }

  /**
   * Escape HTML to prevent XSS
   */
  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Scroll chat container to bottom
   */
  function scrollToBottom(container) {
    container.scrollTop = container.scrollHeight;
  }

  /**
   * Add message to chat container
   */
  function addMessage(text, isUser = false, container) {
    const messageDiv = document.createElement('div');
    messageDiv.className = `message${isUser ? ' user' : ''}`;

    const avatarContent = isUser ? 'You' : config.agentInitials;

    // User messages: escape HTML. Agent messages: render markdown.
    const bubbleContent = isUser
      ? escapeHtml(text)
      : (typeof marked !== 'undefined' ? marked.parse(text) : escapeHtml(text));

    messageDiv.innerHTML = `
      <div class="message-avatar">${avatarContent}</div>
      <div class="message-content">
        <div class="message-bubble${isUser ? '' : ' markdown-content'}">${bubbleContent}</div>
        <div class="message-time">${formatTime()}</div>
      </div>
    `;

    container.appendChild(messageDiv);
    scrollToBottom(container);
    return messageDiv;
  }

  /**
   * Show typing indicator
   * @param {HTMLElement} container - The messages container
   * @param {string} statusText - Optional status text to display
   */
  function showTypingIndicator(container, statusText = null) {
    // Remove any existing typing indicator
    removeTypingIndicator(container);

    const indicator = createTypingIndicator(statusText);
    container.appendChild(indicator);
    scrollToBottom(container);

    return indicator;
  }

  /**
   * Update typing indicator with status text
   * @param {HTMLElement} container - The messages container
   * @param {string} statusText - Status text to display (null for just dots)
   */
  function updateTypingIndicator(container, statusText) {
    const existingIndicator = container.querySelector('.typing-message');
    if (existingIndicator) {
      // Update the existing indicator content
      let contentHtml;
      if (statusText) {
        contentHtml = `
          <div class="status-text-container">
            <span class="status-text">${escapeHtml(statusText)}</span>
            <span class="typing-dots-inline">
              <span class="typing-dot"></span>
              <span class="typing-dot"></span>
              <span class="typing-dot"></span>
            </span>
          </div>
        `;
      } else {
        contentHtml = `
          <div class="typing-dots-container">
            <span class="typing-dot"></span>
            <span class="typing-dot"></span>
            <span class="typing-dot"></span>
          </div>
        `;
      }
      existingIndicator.querySelector('.message-content').innerHTML = contentHtml;
      scrollToBottom(container);
    } else {
      // Create new indicator if none exists
      showTypingIndicator(container, statusText);
    }
  }

  /**
   * Remove typing indicator
   */
  function removeTypingIndicator(container) {
    const indicator = container.querySelector('.typing-message');
    if (indicator) {
      indicator.remove();
    }
  }

  /**
   * Send message to webhook
   */
  async function sendMessage(message) {
    const response = await fetch(config.webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        message: message,
        sessionId: getSessionId()
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Poll for status/response
   */
  async function pollForStatus() {
    const response = await fetch(config.statusUrl, {
      method: 'GET',
      headers: {
        'session_id': getSessionId()
      }
    });

    // Return null for errors (session might not exist in sheet yet)
    if (!response.ok) {
      // Don't throw for 500 errors - just return null to continue polling
      // This handles the case where the session row hasn't been created yet
      return null;
    }

    return response.json();
  }

  /**
   * Start polling for response
   */
  function startPolling(container, onResponse) {
    if (isPolling) return;

    isPolling = true;
    pollingAttempts = 0;

    // Capture the current Response value before starting (to detect new responses)
    const initialResponseCheck = async () => {
      try {
        const statusData = await pollForStatus();
        // Handle both array and object responses
        const latestStatus = Array.isArray(statusData)
          ? (statusData.length > 0 ? statusData[statusData.length - 1] : null)
          : statusData;
        if (latestStatus) {
          lastKnownResponse = latestStatus.Response || null;
        }
      } catch (e) {
        lastKnownResponse = null;
      }
      poll();
    };

    const poll = async () => {
      // Stop if polling was disabled or response already handled
      if (!isPolling || responseHandled) return;

      pollingAttempts++;

      if (pollingAttempts > config.maxPollingAttempts) {
        stopPolling();
        if (!responseHandled) {
          removeTypingIndicator(container);
          addMessage('Sorry, the response is taking too long. Please try again.', false, container);
        }
        return;
      }

      try {
        const statusData = await pollForStatus();

        // Check again after async call in case response was handled while waiting
        if (!isPolling || responseHandled) return;

        // Handle both array and object responses
        const latestStatus = Array.isArray(statusData)
          ? (statusData.length > 0 ? statusData[statusData.length - 1] : null)
          : statusData;

        if (latestStatus) {
          // Check if Response field exists, is not empty, AND is different from the initial response
          const currentResponse = latestStatus.Response ? latestStatus.Response.trim() : '';
          const previousResponse = lastKnownResponse ? lastKnownResponse.trim() : '';

          if (currentResponse !== '' && currentResponse !== previousResponse) {
            stopPolling();
            removeTypingIndicator(container);
            onResponse(latestStatus.Response);
            return;
          }

          // Check if there's a status Message to display while processing (capital M)
          if (latestStatus.Message && latestStatus.Message.trim() !== '') {
            updateTypingIndicator(container, latestStatus.Message);
          } else {
            // No status message, show just the dots
            updateTypingIndicator(container, null);
          }
        }

        // Continue polling if no response yet
        if (!responseHandled) {
          setTimeout(poll, config.pollingInterval);
        }
      } catch (error) {
        console.error('Polling error:', error);
        if (!responseHandled) {
          setTimeout(poll, config.pollingInterval);
        }
      }
    };

    initialResponseCheck();
  }

  /**
   * Stop polling
   */
  function stopPolling() {
    isPolling = false;
    pollingAttempts = 0;
  }

  /**
   * Handle message submission
   */
  async function handleSubmit(input, container, sendButton) {
    const message = input.value.trim();
    if (!message) return;

    // Disable input while processing
    input.disabled = true;
    sendButton.disabled = true;

    // Reset response handled flag for new message
    responseHandled = false;

    // Add user message
    addMessage(message, true, container);
    input.value = '';

    // Show typing indicator immediately
    showTypingIndicator(container);

    // Start polling for status updates (will be stopped when we get the main response)
    startPolling(container, (responseMessage) => {
      // Only handle if not already handled by main API response
      if (responseHandled) return;
      responseHandled = true;

      addMessage(responseMessage, false, container);
      // Re-enable input when we get a response
      input.disabled = false;
      sendButton.disabled = false;
      input.focus();
    });

    try {
      // Send message to webhook and wait for response
      const response = await sendMessage(message);

      // Check if already handled by polling (shouldn't happen but just in case)
      if (responseHandled) return;
      responseHandled = true;

      // Stop polling since we got the response from the main API
      stopPolling();
      removeTypingIndicator(container);

      // Extract the response message from the API response
      const responseMessage = response.output || response.message || response.response || response;

      // Add the response to the chat
      if (typeof responseMessage === 'string' && responseMessage.trim()) {
        addMessage(responseMessage, false, container);
      } else if (typeof responseMessage === 'object') {
        addMessage(JSON.stringify(responseMessage), false, container);
      }

      // Re-enable input
      input.disabled = false;
      sendButton.disabled = false;
      input.focus();
    } catch (error) {
      console.error('Error sending message:', error);
      stopPolling();
      removeTypingIndicator(container);
      addMessage('Sorry, there was an error sending your message. Please try again.', false, container);
      // Re-enable input on error
      input.disabled = false;
      sendButton.disabled = false;
      input.focus();
    }
  }

  /**
   * Reset session - creates a new session ID and clears chat
   */
  function resetSession(messagesContainer) {
    // Stop any ongoing polling
    stopPolling();

    // Generate new session ID
    sessionId = generateSessionId();
    sessionStorage.setItem('chat_session_id', sessionId);

    // Reset last known response
    lastKnownResponse = null;

    // Clear messages container
    if (messagesContainer) {
      messagesContainer.innerHTML = '';

      // Add welcome message if configured
      if (config.welcomeMessage) {
        addMessage(config.welcomeMessage, false, messagesContainer);
      }
    }
  }

  /**
   * Create the floating chat widget HTML
   */
  function createFloatingWidget() {
    const widget = document.createElement('div');
    widget.className = 'chat-widget';
    widget.innerHTML = `
      <button class="chat-toggle" aria-label="Open chat">
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z"/>
          <path d="M7 9h10v2H7zm0-3h10v2H7zm0 6h7v2H7z"/>
        </svg>
        <span class="notification-badge" style="display: none;"></span>
      </button>
      <div class="chat-container">
        <div class="chat-header">
          <div class="chat-header-info">
            <div class="agent-avatar">${config.agentInitials}</div>
            <div class="agent-info">
              <h3>${config.agentName}</h3>
              <div class="agent-status">
                <span class="status-dot"></span>
                <span>${config.agentStatus}</span>
              </div>
            </div>
          </div>
          <div class="chat-actions">
            <button class="chat-action-btn" id="reset-session-btn" title="New conversation">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            </button>
            <button class="chat-action-btn" id="minimize-chat-btn" title="Minimize">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            </button>
          </div>
        </div>
        <div class="chat-messages"></div>
        <div class="chat-input-container">
          <div class="chat-input-wrapper">
            <input type="text" class="chat-input" id="chat-widget-input" placeholder="Type your message..." />
            <button class="chat-send-btn" aria-label="Send message">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    `;
    return widget;
  }

  /**
   * Initialize floating chat widget
   */
  function initFloatingWidget() {
    // Create and append the widget
    floatingWidgetContainer = createFloatingWidget();
    document.body.appendChild(floatingWidgetContainer);

    // Get elements
    const toggleBtn = floatingWidgetContainer.querySelector('.chat-toggle');
    const chatContainer = floatingWidgetContainer.querySelector('.chat-container');
    const messagesContainer = floatingWidgetContainer.querySelector('.chat-messages');
    const input = floatingWidgetContainer.querySelector('.chat-input');
    const sendButton = floatingWidgetContainer.querySelector('.chat-send-btn');
    const resetBtn = floatingWidgetContainer.querySelector('#reset-session-btn');
    const minimizeBtn = floatingWidgetContainer.querySelector('#minimize-chat-btn');

    // Toggle chat open/close
    toggleBtn.addEventListener('click', () => {
      isWidgetOpen = !isWidgetOpen;
      chatContainer.classList.toggle('active', isWidgetOpen);
      toggleBtn.classList.toggle('active', isWidgetOpen);

      if (isWidgetOpen) {
        input.focus();
      }
    });

    // Reset session button (+ button)
    resetBtn.addEventListener('click', () => {
      resetSession(messagesContainer);
    });

    // Minimize button (- button)
    minimizeBtn.addEventListener('click', () => {
      isWidgetOpen = false;
      chatContainer.classList.remove('active');
      toggleBtn.classList.remove('active');
    });

    // Add welcome message
    if (config.welcomeMessage) {
      addMessage(config.welcomeMessage, false, messagesContainer);
    }

    // Handle send button click
    sendButton.addEventListener('click', () => {
      handleSubmit(input, messagesContainer, sendButton);
    });

    // Handle enter key
    input.addEventListener('keypress', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(input, messagesContainer, sendButton);
      }
    });
  }

  /**
   * Initialize inline chat widget (embedded in page)
   */
  function initInlineChat() {
    const inlineChatContainer = document.querySelector('.wecare-hub__inline-chat');
    if (!inlineChatContainer) return;

    // Get config from data attributes if available
    const webhookUrl = inlineChatContainer.dataset.webhookUrl;
    const welcomeMessage = inlineChatContainer.dataset.welcomeMessage;

    if (webhookUrl) config.webhookUrl = webhookUrl;
    if (welcomeMessage) config.welcomeMessage = welcomeMessage;

    const messagesContainer = inlineChatContainer.querySelector('.inline-chat-messages');
    const input = inlineChatContainer.querySelector('.chat-input');
    const sendButton = inlineChatContainer.querySelector('.chat-send-btn');

    // Get or create header action buttons for inline chat
    let headerActions = inlineChatContainer.querySelector('.chat-actions');
    if (!headerActions) {
      const header = inlineChatContainer.querySelector('.inline-chat-header');
      if (header) {
        headerActions = document.createElement('div');
        headerActions.className = 'chat-actions';
        headerActions.innerHTML = `
          <button class="chat-action-btn" id="inline-reset-session-btn" title="New conversation">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </button>
        `;
        header.appendChild(headerActions);
      }
    }

    // Setup reset button for inline chat
    const resetBtn = inlineChatContainer.querySelector('#inline-reset-session-btn');
    if (resetBtn) {
      resetBtn.addEventListener('click', () => {
        resetSession(messagesContainer);
      });
    }

    if (!messagesContainer || !input || !sendButton) return;

    // Add welcome message
    if (config.welcomeMessage) {
      addMessage(config.welcomeMessage, false, messagesContainer);
    }

    // Handle send button click
    sendButton.addEventListener('click', () => {
      handleSubmit(input, messagesContainer, sendButton);
    });

    // Handle enter key
    input.addEventListener('keypress', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(input, messagesContainer, sendButton);
      }
    });
  }

  /**
   * Initialize the chat widget
   */
  function init(options = {}) {
    // Merge options with config
    Object.assign(config, options);

    // Generate session ID
    getSessionId();

    // Initialize floating widget (bottom-right corner icon)
    if (options.floatingWidget !== false) {
      initFloatingWidget();
    }

    // Initialize inline chat if present
    initInlineChat();
  }

  // Public API
  return {
    init: init,
    resetSession: resetSession
  };
})();

// Make it globally available
window.ChatWidget = ChatWidget;
